import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { Seo } from "@/components/seo";
import { SEO_PAGES } from "@/routes/seo";

/**
 * Business details shown on the legal pages. Keep in step with SELLER_LEGAL_NAME / SELLER_ADDRESS
 * (the GST invoice) once the registered entity is final.
 */
export const LEGAL = {
  business: "MarkFeed",
  address: "Bengaluru, Karnataka, India",
  email: "hello@markfeed.site",
  grievanceOfficer: "Grievance Officer, MarkFeed",
  updated: "11 October 2026",
  graceDays: 3,
} as const;

const Mail = () => (
  <a href={`mailto:${LEGAL.email}`} className="font-medium text-brand-600 hover:underline">
    {LEGAL.email}
  </a>
);

function LegalPage({ eyebrow, title, intro, sections }: { eyebrow: string; title: string; intro: ReactNode; sections: [string, ReactNode][] }) {
  const { pathname } = useLocation();
  const seo = SEO_PAGES[pathname];
  return (
    <>
      {seo && <Seo title={seo.title} description={seo.description} path={pathname} />}
      <article className="mx-auto max-w-3xl px-5 py-16 sm:py-20">
        <p className="text-[13px] font-semibold uppercase tracking-wider text-brand-600">{eyebrow}</p>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight text-ink">{title}</h1>
        <p className="mt-3 text-sm text-slate-500">Last updated: {LEGAL.updated}</p>
        <div className="mt-6 text-[16px] leading-relaxed text-slate-600">{intro}</div>
        {sections.map(([heading, body], i) => (
          <section key={heading} className="mt-10">
            <h2 className="text-xl font-semibold tracking-tight text-ink">
              {i + 1}. {heading}
            </h2>
            <div className="mt-3 space-y-3 text-[16px] leading-relaxed text-slate-600 [&_li]:ml-5 [&_li]:list-disc [&_li]:pl-1 [&_ul]:space-y-1.5">{body}</div>
          </section>
        ))}
      </article>
    </>
  );
}

export function Terms() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms & Conditions"
      intro={
        <p>
          These terms are an agreement between you and {LEGAL.business} ("we", "us"). They apply when you visit markfeed.site, create an account, or use the MarkFeed
          dashboard, review links, WordPress plugin or website script (together, the "Service"). By using the Service you agree to them. If you use MarkFeed for
          a company or agency, you accept them on its behalf.
        </p>
      }
      sections={[
        [
          "The Service",
          <p>
            MarkFeed lets you collect visual feedback on websites and designs. Reviewers click on a page to leave comments, screenshots and attachments, and your
            team manages them as tasks. We may improve, change or remove features over time. If we remove something important from a paid plan, we'll tell you
            in advance.
          </p>,
        ],
        [
          "Your account",
          <ul>
            <li>You must be at least 18 years old and give accurate details when you sign up.</li>
            <li>Keep your login safe. You're responsible for what happens under your account and your team's accounts.</li>
            <li>Tell us straight away at <Mail /> if you think someone has accessed your account without permission.</li>
          </ul>,
        ],
        [
          "Plans, prices and payment",
          <>
            <ul>
              <li>
                The Starter plan is free. Paid plans and their limits are listed on our <Link to="/pricing" className="font-medium text-brand-600 hover:underline">pricing page</Link>.
              </li>
              <li>
                Prices are in Indian Rupees (₹). Where GST applies, it's added at checkout and shown before you pay. You receive an invoice for every payment,
                available under Billing.
              </li>
              <li>Payments are processed by Razorpay (UPI, cards, net banking and wallets). We never see or store your full card or bank details.</li>
              <li>
                Plans are prepaid for a billing period. <strong>We never charge you automatically</strong>: you renew yourself from Billing. If you don't renew,
                your plan stays usable for {LEGAL.graceDays} days after it ends, then your organisation moves to the free Starter plan.
              </li>
              <li>We may change prices. Changes never affect a period you've already paid for, and we'll announce them before they apply to your next payment.</li>
            </ul>
            <p>
              Cancellations, upgrades, downgrades and refunds are covered by our{" "}
              <Link to="/refund-policy" className="font-medium text-brand-600 hover:underline">Refund &amp; Return Policy</Link>.
            </p>
          </>,
        ],
        [
          "Your content",
          <>
            <p>
              You own everything you and your reviewers put into MarkFeed: comments, screenshots, files, and the websites and designs you review ("your content").
              You give us permission to store, copy and display your content only as needed to run the Service for you.
            </p>
            <p>
              When you invite clients or others to review, you're responsible for having the right to collect their feedback and the details they enter (such as
              their name and email). We handle that information on your behalf as described in our{" "}
              <Link to="/privacy" className="font-medium text-brand-600 hover:underline">Privacy Policy</Link>.
            </p>
          </>,
        ],
        [
          "Acceptable use",
          <>
            <p>You agree not to:</p>
            <ul>
              <li>install the MarkFeed script or plugin on a website you don't own or have permission to change;</li>
              <li>upload anything illegal, infringing, malicious (such as malware) or that you don't have the right to share;</li>
              <li>use review links or previews to deceive people, collect passwords or harm a website or its visitors;</li>
              <li>try to break, overload, probe or get around the security or limits of the Service;</li>
              <li>copy, resell or reverse-engineer the Service, except where the law allows it.</li>
            </ul>
          </>,
        ],
        [
          "Other services",
          <p>
            Some features connect to other services, such as Google sign-in, Razorpay payments, and email or WhatsApp notifications. Those services have their own
            terms. We aren't responsible for them, but we choose them carefully.
          </p>,
        ],
        [
          "Availability and support",
          <p>
            We work hard to keep MarkFeed fast and available, but we can't promise it will never be interrupted, for example during maintenance or problems with our
            hosting providers. Support is by email for all plans and on WhatsApp for paid plans.
          </p>,
        ],
        [
          "Suspension and closing your account",
          <p>
            You can stop using MarkFeed at any time and ask us to delete your account at <Mail />. We may suspend or close an account that breaks these terms,
            puts other users or the Service at risk, or where the law requires it. Where we reasonably can, we'll warn you first and give you a chance to export
            your content.
          </p>,
        ],
        [
          "Disclaimer and limit of liability",
          <>
            <p>
              The Service is provided "as is". To the extent the law allows, we don't give warranties that it will be error-free or suit a particular purpose.
            </p>
            <p>
              To the extent the law allows, we aren't liable for indirect or consequential losses such as lost profits, revenue or data. Our total liability for any
              claim about the Service is limited to the amount you paid us in the 12 months before the claim. Nothing here limits liability that can't be limited by
              law.
            </p>
          </>,
        ],
        [
          "Indemnity",
          <p>
            If someone makes a claim against us because of your content, or because you used the Service in breach of these terms or the law, you agree to cover the
            reasonable costs of that claim.
          </p>,
        ],
        [
          "Governing law",
          <p>
            These terms are governed by the laws of India. Disputes will be heard by the courts at Bengaluru, Karnataka. Before going to court, please write to us at{" "}
            <Mail /> so we can try to resolve it.
          </p>,
        ],
        [
          "Changes to these terms",
          <p>
            We may update these terms. We'll change the date at the top and, for important changes, tell account owners by email or in the app before they take
            effect. If you keep using MarkFeed after that, the new terms apply.
          </p>,
        ],
        [
          "Contact",
          <p>
            {LEGAL.business}, {LEGAL.address}. Email <Mail /> or use our <Link to="/contact" className="font-medium text-brand-600 hover:underline">contact form</Link>.
          </p>,
        ],
      ]}
    />
  );
}

export function Privacy() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      intro={
        <p>
          This policy explains what personal data {LEGAL.business} collects, why, and the choices you have. It applies to markfeed.site, the MarkFeed app, review
          links, the WordPress plugin and the website script. It is written to meet India's Digital Personal Data Protection Act, 2023 and the Information
          Technology Act, 2000 and its rules.
        </p>
      }
      sections={[
        [
          "What we collect",
          <ul>
            <li>
              <strong>Account details:</strong> your name, email, phone number (if you sign in with a one-time code or turn on WhatsApp notifications), and your
              password, which we store only in hashed form. If you use Google sign-in, we receive your name, email and profile picture from Google.
            </li>
            <li>
              <strong>Billing details:</strong> your organisation's legal name, address and GSTIN (if you add them), what you bought and your payment status.
              Razorpay processes the payment itself; we never receive your full card, UPI or bank details.
            </li>
            <li>
              <strong>Feedback content:</strong> the websites and designs you add, and the comments, screenshots, attachments and approvals that you and your
              reviewers leave.
            </li>
            <li>
              <strong>Reviewer details:</strong> the name and email a reviewer types in when they first leave feedback.
            </li>
            <li>
              <strong>Technical details:</strong> with each comment, the page address and title, browser, operating system and screen size, so your team can
              reproduce the issue. We also log sign-ins and important account actions, with IP addresses, to keep accounts secure.
            </li>
            <li>
              <strong>Messages to us:</strong> what you send through the contact form, support tickets or email.
            </li>
          </ul>,
        ],
        [
          "How we use it",
          <>
            <ul>
              <li>to run the Service: showing feedback to the right people, sending the notifications you've chosen, and keeping your account working;</li>
              <li>to take payments and issue GST invoices;</li>
              <li>to keep MarkFeed secure and prevent abuse;</li>
              <li>to answer your questions and improve the product;</li>
              <li>to send important account messages, such as billing or security notices.</li>
            </ul>
            <p>
              We don't sell personal data, and we don't use it for advertising. The MarkFeed script on your website stays inactive for normal visitors in
              reviewer-only mode, and it never tracks your visitors.
            </p>
          </>,
        ],
        [
          "Data our customers collect",
          <p>
            When an agency, freelancer or company uses MarkFeed to collect feedback from you, they decide what is collected and why, and we process it on their
            behalf. If you reviewed a site for one of our customers and want your details changed or removed, contact them. You can also write to us and we'll help.
          </p>,
        ],
        [
          "Who we share it with",
          <>
            <p>We share personal data only with service providers that help us run MarkFeed, and only what each one needs:</p>
            <ul>
              <li>hosting, database and file storage providers;</li>
              <li>email, SMS and WhatsApp delivery providers for one-time codes and notifications;</li>
              <li>Razorpay, for payments;</li>
              <li>Google, if you choose Google sign-in.</li>
            </ul>
            <p>
              We may also disclose data when the law requires it, to protect people's safety or our rights, or as part of a merger or sale of the business, under the
              same protections. Some providers may process data outside India, with safeguards that the law requires.
            </p>
          </>,
        ],
        [
          "Cookies",
          <p>
            We use only essential cookies: one that keeps you signed in, and a short-lived one that protects Google sign-in. We don't use advertising or analytics
            cookies, so there's nothing to opt out of.
          </p>,
        ],
        [
          "How long we keep it",
          <p>
            We keep your data while your account is open. When you ask us to delete your account, we delete your account and feedback content within 30 days, apart
            from records the law requires us to keep, such as invoices and payment records for tax purposes, and short-lived backups that expire on their own.
          </p>,
        ],
        [
          "Security",
          <p>
            Data is encrypted in transit (HTTPS), passwords are hashed, review links can be revoked at any time, and access inside each organisation is limited by
            role. No system is perfectly secure; if a breach affects your data, we'll inform you and the authorities as the law requires.
          </p>,
        ],
        [
          "Your rights",
          <p>
            You can ask to see, correct, update or delete your personal data, withdraw consent you've given, and nominate someone to act for you. You can update most
            details yourself under Settings. For anything else, email <Mail />. We'll reply within 30 days.
          </p>,
        ],
        [
          "Children",
          <p>MarkFeed is for businesses and is not meant for anyone under 18. We don't knowingly collect data from children.</p>,
        ],
        [
          "Grievance Officer",
          <p>
            If you have a concern about how we handle your data, contact our {LEGAL.grievanceOfficer}, {LEGAL.address}, at <Mail />. We'll acknowledge it within 48
            hours and try to resolve it within 30 days. If you're not satisfied, you may complain to the Data Protection Board of India.
          </p>,
        ],
        [
          "Changes",
          <p>We'll update the date at the top when this policy changes, and email account owners about important changes before they take effect.</p>,
        ],
      ]}
    />
  );
}

export function RefundPolicy() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Refund & Return Policy"
      intro={
        <p>
          MarkFeed is an online software service. There's nothing physical to ship or return. This policy explains how cancellations, plan changes and refunds
          work. Try the free Starter plan first to make sure MarkFeed suits you before you pay.
        </p>
      }
      sections={[
        [
          "No automatic charges",
          <p>
            Paid plans are prepaid for one billing period at a time. We never charge your card, UPI or bank account automatically: a plan only renews when you pay
            for the next period yourself from Billing.
          </p>,
        ],
        [
          "Cancelling",
          <p>
            You can cancel any time from Billing. Your plan stays active until the end of the period you've paid for and isn't renewed. After that, your
            organisation moves to the free Starter plan; you keep your account. We don't refund the unused part of a period after cancelling.
          </p>,
        ],
        [
          "Upgrading and downgrading",
          <ul>
            <li>
              <strong>Upgrade:</strong> it takes effect straight away, and the unused value of your current plan is deducted from the price of the new plan, so
              you only pay the difference (plus GST, where it applies).
            </li>
            <li>
              <strong>Downgrade:</strong> it's scheduled for the end of the period you've paid for. You keep your current plan's features until then, and
              nothing is charged or refunded.
            </li>
          </ul>,
        ],
        [
          "When we do refund",
          <>
            <p>We refund the full amount, including GST, if:</p>
            <ul>
              <li>you were charged twice for the same purchase;</li>
              <li>you were charged but your plan wasn't activated and we can't fix it;</li>
              <li>you were charged because of an error on our side.</li>
            </ul>
            <p>
              We may also refund in other situations where it's fair, for example a long outage on our side. Ask us and we'll look at it case by case.
            </p>
          </>,
        ],
        [
          "How to ask for a refund",
          <p>
            Email <Mail /> within 7 days of the payment, with the email on your account and the payment or invoice number (shown under Billing). We'll reply within
            2 working days.
          </p>,
        ],
        [
          "When you'll receive it",
          <p>
            Approved refunds go back to the original payment method (UPI, card, net banking or wallet) through Razorpay within 5–7 working days. Your bank may take a
            few more days to show it. Where GST rules require it, we'll issue a credit note.
          </p>,
        ],
        [
          "If a plan lapses",
          <p>
            If you don't renew, your plan stays usable for {LEGAL.graceDays} days after it ends. After that, your organisation moves to the free Starter plan. No money
            is taken, so there's nothing to refund.
          </p>,
        ],
        [
          "Contact",
          <p>
            Questions about a payment? Email <Mail /> or use our <Link to="/contact" className="font-medium text-brand-600 hover:underline">contact form</Link>.
          </p>,
        ],
      ]}
    />
  );
}
